import * as Sentry from '@sentry/browser'
import { Card, Layout, Result } from 'antd'
import React, { Component, ReactNode, ErrorInfo } from 'react'
import { useTranslation } from 'react-i18next'
import { CONTENT_STYLE } from '../../Styles'

interface ErrorBoundaryState {
  hasError?: boolean
  eventId?: string
}

interface Props {
  eventId: string
}

const { Content } = Layout

const ErrorUI: React.FC<Props> = ({ eventId }): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Layout>
      <Content style={{ ...CONTENT_STYLE, margin: '20px auto', minHeight: '100vh' }}>
        <Card>
          <Result
            status='error'
            title={t('ErrorBoundary.title', 'Something went wrong.')}
            subTitle={t('ErrorBoundary.subTitle', 'Please try again later.')}
          />
        </Card>
      </Content>
    </Layout>
  )
}

class ErrorBoundary extends Component<ErrorBoundaryState> {
  public static getDerivedStateFromError (): { hasError: boolean } {
    return { hasError: true }
  }

  public state: ErrorBoundaryState = {
    eventId: null,
    hasError: false,
  }

  public componentDidCatch (error: Error, errorInfo: ErrorInfo): void {
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo as unknown as Record<string, unknown>)
      const eventId = Sentry.captureException(error)
      this.setState({ eventId })
    })
  }

  public render (): ReactNode {
    const { hasError, eventId } = this.state

    if (hasError) {
      return <ErrorUI {...{ eventId }} />
    }

    return this.props.children
  }
}

export default ErrorBoundary
